/**
 * FlashcardReviewSession — wrapper that drives a full FSRS review session.
 *
 * Flow:
 *   1. Call GET /scenario-os/adaptive/review-queue/{learnerId} to get due cards
 *   2. Filter to scene_type="flashcard" items (cards have a scene_id reference)
 *   3. Load each flashcard scene document from /scenario-os/coach/scenes/{sceneId}
 *   4. Render them sequentially in FlashcardPlayer
 *   5. Show progress: "Card 3 of 7"
 *   6. After last card: summary screen "Great work! You reviewed N skills today."
 *
 * Props:
 *   learnerId  {string}   — learner identifier
 *   domain     {string}   — domain filter (optional; default "ai")
 *   onExit     {function} — called when learner dismisses the session
 */

import React, { useState, useEffect, useCallback } from 'react';
import FlashcardPlayer from './FlashcardPlayer';

const API_BASE = '/scenario-os';
const TOKEN_KEY = 'scenario_os_token';

function getToken() {
  return (
    localStorage.getItem(TOKEN_KEY) ||
    sessionStorage.getItem(TOKEN_KEY) ||
    ''
  );
}

function authHeaders() {
  const token = getToken();
  return {
    'Content-Type': 'application/json',
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  };
}

async function fetchReviewQueue(learnerId, domain) {
  const url = `${API_BASE}/adaptive/review-queue/${encodeURIComponent(learnerId)}?domain=${encodeURIComponent(domain || '')}`;
  const res = await fetch(url, { headers: authHeaders() });
  if (!res.ok) throw new Error(`Review queue fetch failed: ${res.status}`);
  return res.json();
}

async function fetchFlashcardScene(skillId) {
  // Use the learner-accessible adaptive endpoint which queries the
  // flashcard_scenes collection and normalises question/answer fields.
  const res = await fetch(
    `${API_BASE}/adaptive/flashcard/${encodeURIComponent(skillId)}`,
    { headers: authHeaders() }
  );
  if (!res.ok) return null;
  return res.json();
}

export default function FlashcardReviewSession({ learnerId, domain = 'ai', onExit }) {
  const [phase, setPhase] = useState('loading'); // loading | reviewing | complete | empty | error
  const [scenes, setScenes] = useState([]);
  const [index, setIndex] = useState(0);
  const [reviewedCount, setReviewedCount] = useState(0);
  const [errorMsg, setErrorMsg] = useState('');

  useEffect(() => {
    let cancelled = false;

    async function loadQueue() {
      try {
        const queueData = await fetchReviewQueue(learnerId, domain);
        const queue = queueData.queue || [];

        // All items in the FSRS queue are valid flashcard entries — skip scene_type filter
        if (!queue.length) {
          if (!cancelled) setPhase('empty');
          return;
        }

        // Load scene documents for each item; fall back to a synthetic card if unavailable
        const loadedScenes = [];
        for (const item of queue) {
          const sceneId = item.scene_id || item.skill_id;
          if (!sceneId) continue;
          const scene = await fetchFlashcardScene(sceneId);
          if (scene && scene.scene_type === 'flashcard') {
            loadedScenes.push(scene);
          } else {
            // Build a minimal synthetic flashcard from the FSRS queue item data
            const skillLabel = (item.skill_id || '').replace(/_/g, ' ');
            const overdueLine = item.days_overdue > 0
              ? `\nOverdue: ${item.days_overdue} day${item.days_overdue !== 1 ? 's' : ''}`
              : '';
            const retLine = item.retrievability != null
              ? `\nRetrievability: ${Math.round(item.retrievability * 100)}%`
              : '';
            loadedScenes.push({
              scene_id: item.skill_id,
              scene_type: 'flashcard',
              domain_id: item.domain,
              domain_metadata: {
                skill_id: item.skill_id,
                front_content: `**${skillLabel || item.skill_id}**`,
                back_content: `Domain: ${item.domain || 'N/A'}${retLine}${overdueLine}`,
              },
            });
          }
        }

        if (!cancelled) {
          if (loadedScenes.length === 0) {
            setPhase('empty');
          } else {
            setScenes(loadedScenes);
            setIndex(0);
            setPhase('reviewing');
          }
        }
      } catch (err) {
        if (!cancelled) {
          setErrorMsg(err.message || 'Failed to load review queue');
          setPhase('error');
        }
      }
    }

    loadQueue();
    return () => { cancelled = true; };
  }, [learnerId, domain]);

  const handleCardComplete = useCallback(() => {
    setReviewedCount((n) => n + 1);
    if (index + 1 >= scenes.length) {
      setPhase('complete');
    } else {
      setIndex((i) => i + 1);
    }
  }, [index, scenes.length]);

  // ── Loading ────────────────────────────────────────────────────────────
  if (phase === 'loading') {
    return (
      <div style={centerStyle}>
        <div style={spinnerStyle} />
        <p style={loadingTextStyle}>Loading your review queue…</p>
      </div>
    );
  }

  // ── Error ──────────────────────────────────────────────────────────────
  if (phase === 'error') {
    return (
      <div style={centerStyle}>
        <p style={errorTextStyle}>⚠ {errorMsg}</p>
        {onExit && (
          <button style={exitBtnStyle} onClick={onExit}>
            Close
          </button>
        )}
      </div>
    );
  }

  // ── Empty ──────────────────────────────────────────────────────────────
  if (phase === 'empty') {
    return (
      <div style={centerStyle}>
        <div style={completionIconStyle}>🎉</div>
        <h2 style={completionHeadingStyle}>You're all caught up!</h2>
        <p style={completionSubStyle}>No flashcard reviews due right now. Check back tomorrow.</p>
        {onExit && (
          <button style={exitBtnStyle} onClick={onExit}>
            Back to Learning
          </button>
        )}
      </div>
    );
  }

  // ── Complete ───────────────────────────────────────────────────────────
  if (phase === 'complete') {
    return (
      <div style={centerStyle}>
        <div style={completionIconStyle}>⭐</div>
        <h2 style={completionHeadingStyle}>Great work!</h2>
        <p style={completionSubStyle}>
          You reviewed {reviewedCount} skill{reviewedCount !== 1 ? 's' : ''} today.
        </p>
        {onExit && (
          <button style={exitBtnStyle} onClick={onExit}>
            Back to Learning
          </button>
        )}
      </div>
    );
  }

  // ── Reviewing ──────────────────────────────────────────────────────────
  const currentScene = scenes[index];
  const total = scenes.length;

  return (
    <div style={sessionContainerStyle}>
      {/* Progress header */}
      <div style={progressHeaderStyle}>
        <span style={progressTextStyle}>Card {index + 1} of {total}</span>
        <div style={progressBarBgStyle}>
          <div
            style={{
              ...progressBarFillStyle,
              width: `${((index + 1) / total) * 100}%`,
            }}
          />
        </div>
        {onExit && (
          <button style={exitLinkStyle} onClick={onExit}>
            End Session
          </button>
        )}
      </div>

      {/* Card player */}
      <FlashcardPlayer
        key={currentScene.scene_id || currentScene.domain_metadata?.skill_id || index}
        scene={currentScene}
        learnerId={learnerId}
        onComplete={handleCardComplete}
      />
    </div>
  );
}

// ── Styles ────────────────────────────────────────────────────────────────────

const centerStyle = {
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  minHeight: '320px',
  padding: '32px 16px',
  gap: '16px',
  textAlign: 'center',
};

const spinnerStyle = {
  width: '36px',
  height: '36px',
  border: '3px solid #e2e8f0',
  borderTopColor: '#4f46e5',
  borderRadius: '50%',
  animation: 'spin 0.8s linear infinite',
};

const loadingTextStyle = {
  color: '#718096',
  fontSize: '15px',
};

const errorTextStyle = {
  color: '#e53e3e',
  fontSize: '15px',
};

const completionIconStyle = {
  fontSize: '48px',
};

const completionHeadingStyle = {
  fontSize: '24px',
  fontWeight: 700,
  color: '#1a202c',
  margin: 0,
};

const completionSubStyle = {
  fontSize: '16px',
  color: '#718096',
  margin: 0,
};

const exitBtnStyle = {
  marginTop: '8px',
  padding: '12px 28px',
  background: '#4f46e5',
  color: '#fff',
  border: 'none',
  borderRadius: '8px',
  fontSize: '15px',
  fontWeight: 600,
  cursor: 'pointer',
};

const sessionContainerStyle = {
  display: 'flex',
  flexDirection: 'column',
  width: '100%',
  maxWidth: '720px',
  margin: '0 auto',
  padding: '0 8px',
  boxSizing: 'border-box',
};

const progressHeaderStyle = {
  display: 'flex',
  alignItems: 'center',
  gap: '12px',
  padding: '12px 4px',
  borderBottom: '1px solid #e2e8f0',
  marginBottom: '16px',
};

const progressTextStyle = {
  fontSize: '13px',
  color: '#718096',
  whiteSpace: 'nowrap',
  minWidth: '72px',
};

const progressBarBgStyle = {
  flex: 1,
  height: '6px',
  background: '#e2e8f0',
  borderRadius: '3px',
  overflow: 'hidden',
};

const progressBarFillStyle = {
  height: '100%',
  background: 'linear-gradient(90deg, #4f46e5, #7c3aed)',
  borderRadius: '3px',
  transition: 'width 0.3s ease',
};

const exitLinkStyle = {
  background: 'none',
  border: 'none',
  color: '#a0aec0',
  fontSize: '13px',
  cursor: 'pointer',
  whiteSpace: 'nowrap',
};
